import { useState, useEffect } from "react";

import { getAllReportsData } from "../../../services/profileService";
import { useAuth } from "../../../contexts/AuthContext";
import { useToast } from "../../../contexts/ToastContext";
import React from "react";
import { updateReportStatus } from "../../../services/eventService";

export interface Event {
  id: number;
  title: string;
  datetime: string;
  location_city: string;
}

export interface User {
  id: number;
  name: string;
  email: string;
}

export interface Report {
  id: string;
  admin_comment: string | null;
  comment: string;
  createdAt: string;
  reason: string;
  reported_at_formatted: string;
  reported_type: string;
  reported_type_label: string;
  status: string;
  status_label: string;
  Event: Event;
  reporter: User;
  reportedHost: User | null;
  resolvedBy: User | null;
}

export const REPORT_STATUS = {
  RESOLVED: "resolved",
  REJECTED: "rejected",
} as const;

export type ReportStatus = (typeof REPORT_STATUS)[keyof typeof REPORT_STATUS];

interface ReportForm {
  report_id: number;
  event_id: number;
  status: ReportStatus;
  comment: string;
}

const useReportListLogic = () => {
  const { isAuthenticated, user } = useAuth();
  const { showSuccessToast, showErrorToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState<Report[]>([]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const res = await getAllReportsData();
      console.log("Reports data:", res.data.data);
      setReports(res.data.data);
    } catch (err) {
      console.error("Error fetching bookings:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const [reportLoading, setReportLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [reportForm, setReportForm] = useState<ReportForm>({
    report_id: 0,
    event_id: 0,
    status: REPORT_STATUS.RESOLVED,
    comment: "",
  });

  const onInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: keyof ReportForm
  ) => {
    setReportForm({ ...reportForm, [field]: e.target.value });
  };

  const [showReportDialog, setShowReportDialog] = React.useState(false);

  const showDialog = (report_id: string, event_id: number) => {
    resetForm(Number(report_id), event_id);
    setShowReportDialog(true);
  };

  const hideDialog = () => {
    resetForm(0, 0);
    setShowReportDialog(false);
  };

  const resetForm = (report_id: number, event_id: number) => {
    setReportForm({
      report_id: report_id,
      event_id: event_id,
      status: REPORT_STATUS.RESOLVED,
      comment: "",
    });
    setSubmitted(false);
  };

  const handleReportEvent = async () => {
    setSubmitted(true);

    if (!reportForm.status || !reportForm.comment) {
      return;
    }

    try {
      setReportLoading(true);

      const payload = {
        event_id: reportForm.event_id,
        report_id: reportForm.report_id,
        status: reportForm.status,
        comment: reportForm.comment,
      };

      console.log("Report update payload:", payload);

      const response = await updateReportStatus(
        reportForm.event_id,
        reportForm.report_id,
        payload
      );

      showSuccessToast(response.data.message);
      fetchReports();
      hideDialog();
    } catch (error: any) {
      showErrorToast(
        error?.response?.data?.message || "Failed to report issue."
      );
    } finally {
      setReportLoading(false);
    }
  };

  return {
    reports,
    loading,
    reportLoading,
    showDialog,
    hideDialog,
    showReportDialog,
    setReportForm,
    reportForm,
    onInputChange,
    submitted,
    setSubmitted,
    handleReportEvent,
  };
};

export default useReportListLogic;
